/**
 * Saved dock views — pure model + namespaced localStorage persistence (no React).
 *
 * A view captures the full control-row state of one tab: {sort, window, search, chips, groupBy}.
 * Presets ship in the tab configs (GridConfig.presetViews — not deletable); user views persist
 * in localStorage under a key NAMESPACED per base + block installation, so two bases (or two
 * copies of the extension) sharing an origin can never read each other's views. The same
 * namespace carries the dock's other persisted keys (last tab), with a one-time migration off
 * the legacy unnamespaced 'lfe.dockTab' an audit flagged.
 *
 * Storage is injected (StorageLike) so every function here is testable in node.
 */
import { sanitizeGroupBy } from './groupTree';

export interface DockView {
  name: string;
  /** Shipped in config — applied like any view but not deletable. */
  preset?: boolean;
  sort?: { key: string; dir: 'asc' | 'desc' };
  /** Window key ('48h', 'next14', …) — omitted = All time. */
  window?: string;
  /** Quick-filter text, including field:value facets. */
  search?: string;
  /** Selected KPI chip labels (AND-combined). */
  chips?: string[];
  /** Group option key(s) — a single key (legacy) or an ORDERED stack (outer→inner). Omitted = no
   *  grouping. Persisted views written before multi-level grouping carry a bare string; sanitizeView
   *  normalizes both shapes to the array form. */
  groupBy?: string | string[];
}

export interface StorageLike {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

// ---- namespace -----------------------------------------------------------------------------
/** Per-installation key prefix: lfe.<baseId>.<blockId>.dock (blockId omitted when unknown). */
export const dockNamespace = (baseId: string, blockId?: string): string =>
  `lfe.${baseId || 'base'}${blockId ? '.' + blockId : ''}.dock`;

/** Best-effort block-installation id — the interface SDK doesn't expose one publicly, but the
 *  extension iframe URL carries it. Graceful '' fallback keeps the namespace base-scoped. */
export function getBlockIdBestEffort(): string {
  try {
    const m = String(window.location.href).match(/blk[A-Za-z0-9]{8,}/);
    return m ? m[0] : '';
  } catch { return ''; }
}

/** One-time legacy-key migration: copy value to the namespaced key (unless one already exists),
 *  then remove the legacy key. Idempotent; storage failures (private mode) are swallowed. */
export function migrateKey(store: StorageLike, legacy: string, namespaced: string): void {
  try {
    const v = store.getItem(legacy);
    if (v == null) return;
    if (store.getItem(namespaced) == null) store.setItem(namespaced, v);
    store.removeItem(legacy);
  } catch { /* private mode */ }
}

// ---- left-rail personal saved views (map lens+filter views, NOT dock grid views) -------------
/** Namespaced localStorage key for the rail's PERSONAL saved views (shared views live in
 *  globalConfig and need no namespacing — globalConfig is already per base). Same shape as
 *  dockNamespace: lfe.<baseId>.<blockId>.savedViews.v1 (blockId omitted when unknown). */
export const railViewsKey = (baseId: string, blockId?: string): string =>
  `lfe.${baseId || 'base'}${blockId ? '.' + blockId : ''}.savedViews.v1`;

/** Copy-don't-delete migration for the rail's personal views off the legacy un-namespaced
 *  'lfeSavedViews.v1' key: copy to the namespaced key on first load, but LEAVE the legacy key
 *  in place as a backup (unlike migrateKey) — if this installation's namespace ever changes
 *  (block reinstall → new blockId), the owner's views can migrate again instead of being lost.
 *  Never clobbers an existing namespaced value; idempotent; storage failures swallowed. */
export function migrateKeyKeepLegacy(store: StorageLike, legacy: string, namespaced: string): void {
  try {
    const v = store.getItem(legacy);
    if (v == null) return;
    if (store.getItem(namespaced) == null) store.setItem(namespaced, v);
  } catch { /* private mode */ }
}

// ---- persistence ---------------------------------------------------------------------------
const viewsKey = (ns: string, tab: string) => `${ns}.views.${tab}`;

export function loadViews(store: StorageLike, ns: string, tab: string): DockView[] {
  try {
    const raw = store.getItem(viewsKey(ns, tab));
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter(x => x && typeof x.name === 'string' && x.name.trim()) : [];
  } catch { return []; }
}

export function saveViews(store: StorageLike, ns: string, tab: string, views: DockView[]): void {
  try { store.setItem(viewsKey(ns, tab), JSON.stringify(views)); } catch { /* private mode */ }
}

/** Replace-by-name insert (saving "Morning triage" twice overwrites, never duplicates). */
export const upsertView = (views: DockView[], v: DockView): DockView[] =>
  [...views.filter(x => x.name !== v.name), { ...v, preset: undefined }];

export const removeView = (views: DockView[], name: string): DockView[] =>
  views.filter(x => x.name !== name);

// ---- application ---------------------------------------------------------------------------
/** What the current config can actually express — applying a view drops anything stale
 *  (a renamed column, a removed KPI) instead of wedging the grid into an invalid state. */
export interface ViewVocab {
  sortKeys: ReadonlySet<string>;
  windowKeys: ReadonlySet<string>;
  kpiLabels: ReadonlySet<string>;
  groupKeys: ReadonlySet<string>;
}

export function sanitizeView(v: DockView, vocab: ViewVocab): DockView {
  return {
    name: v.name,
    preset: v.preset,
    sort: v.sort && vocab.sortKeys.has(v.sort.key)
      ? { key: v.sort.key, dir: v.sort.dir === 'desc' ? 'desc' : 'asc' } : undefined,
    window: v.window && vocab.windowKeys.has(v.window) ? v.window : undefined,
    search: typeof v.search === 'string' && v.search.trim() ? v.search : undefined,
    chips: (v.chips || []).filter(c => vocab.kpiLabels.has(c)),
    // legacy single string OR ordered array → array of live group keys (deduped, capped); empty → undefined
    groupBy: ((g) => g.length ? g : undefined)(sanitizeGroupBy(v.groupBy, vocab.groupKeys)),
  };
}
